import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Trophy, Users, Target, Star, Crown, CheckCircle, Info, AlertCircle } from 'lucide-react';

export function ChallengesGuide() {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
    <div className="border border-white/10 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <h3 className="text-lg text-white font-medium">{title}</h3>
        {expandedSection === id ? (
          <ChevronUp className="w-5 h-5 text-white/70" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/70" />
        )}
      </button>
      {expandedSection === id && (
        <div className="p-4 bg-white/[0.02] space-y-4">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl text-white font-brand font-light italic mb-2">Baking Challenges</h2>
        <p className="text-white/80">
          Join community challenges to improve your skills, compete with other bakers, and earn recognition.
        </p>
      </div>

      <Section id="overview" title="What are Challenges?">
        <p className="text-white/80 mb-4">
          Challenges are community-driven baking goals where bakers work toward specific objectives together.
          They're designed to push your skills, encourage experimentation, and connect you with fellow sourdough enthusiasts.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Target className="w-5 h-5 text-green-400" />
              <h4 className="text-white font-medium">Set Goals</h4>
            </div>
            <p className="text-white/70 text-sm">
              Each challenge has specific objectives to achieve, like mastering a technique or trying a new flour type.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-blue-400" />
              <h4 className="text-white font-medium">Community</h4>
            </div>
            <p className="text-white/70 text-sm">
              See how many bakers have joined, completed the challenge, and compare your progress with others.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              <h4 className="text-white font-medium">Recognition</h4>
            </div>
            <p className="text-white/70 text-sm">
              Complete challenges to earn badges and build your baking portfolio. Track your achievements over time.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Star className="w-5 h-5 text-purple-400" />
              <h4 className="text-white font-medium">Learn & Grow</h4>
            </div>
            <p className="text-white/70 text-sm">
              Challenges are structured learning experiences that guide you toward mastering specific techniques.
            </p>
          </div>
        </div>
      </Section>

      <Section id="types" title="Types of Challenges">
        <p className="text-white/80 mb-4">
          Breadlify offers various challenge types to suit different skill levels and interests:
        </p>

        <div className="space-y-3">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Technique Challenges</h4>
                <p className="text-white/70 text-sm mb-2">
                  Focus on mastering specific techniques like lamination, coil folds, or advanced scoring patterns.
                </p>
                <p className="text-white/60 text-xs italic">
                  Example: "Perfect Your Scoring: Create 5 loaves with complex scoring patterns"
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Trophy className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Monthly Themes</h4>
                <p className="text-white/70 text-sm mb-2">
                  Themed challenges that rotate each month, encouraging exploration of different styles or ingredients.
                </p>
                <p className="text-white/60 text-xs italic">
                  Example: "Rye Revolution: Bake 3 different breads using rye flour"
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Community Challenges</h4>
                <p className="text-white/70 text-sm mb-2">
                  Created by admin or featured bakers, these challenges bring the community together around shared goals.
                </p>
                <p className="text-white/60 text-xs italic">
                  Example: "Holiday Baking: Share your festive sourdough creations"
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Skill Level Challenges</h4>
                <p className="text-white/70 text-sm mb-2">
                  Designed for specific experience levels, from beginner-friendly to expert-only challenges.
                </p>
                <p className="text-white/60 text-xs italic">
                  Example: "Beginner's First Five: Complete your first 5 sourdough loaves"
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section id="joining" title="How to Join a Challenge">
        <p className="text-white/80 mb-4">
          Joining a challenge is simple. Follow these steps:
        </p>

        <ol className="space-y-4">
          <li className="flex items-start gap-4">
            <span className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-sm text-amber-400 font-bold flex-shrink-0">1</span>
            <div>
              <p className="text-white font-medium mb-1">Browse Available Challenges</p>
              <p className="text-white/70 text-sm">
                Navigate to the Challenges tab to see all active challenges. You'll see:
              </p>
              <ul className="text-white/60 text-sm mt-2 space-y-1 ml-4">
                <li>• Challenge name and description</li>
                <li>• Number of participants</li>
                <li>• Difficulty level</li>
                <li>• Time commitment required</li>
                <li>• Deadline (if applicable)</li>
              </ul>
            </div>
          </li>

          <li className="flex items-start gap-4">
            <span className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-sm text-amber-400 font-bold flex-shrink-0">2</span>
            <div>
              <p className="text-white font-medium mb-1">View Challenge Details</p>
              <p className="text-white/70 text-sm">
                Click on any challenge card to see full details including:
              </p>
              <ul className="text-white/60 text-sm mt-2 space-y-1 ml-4">
                <li>• Detailed requirements and objectives</li>
                <li>• Tips for success</li>
                <li>• List of current participants</li>
                <li>• Completion criteria</li>
              </ul>
            </div>
          </li>

          <li className="flex items-start gap-4">
            <span className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-sm text-amber-400 font-bold flex-shrink-0">3</span>
            <div>
              <p className="text-white font-medium mb-1">Click "Join Challenge"</p>
              <p className="text-white/70 text-sm">
                Once you've read the details and are ready to commit, click the "Join Challenge" button.
                You'll be added to the list of participants immediately.
              </p>
            </div>
          </li>

          <li className="flex items-start gap-4">
            <span className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-sm text-amber-400 font-bold flex-shrink-0">4</span>
            <div>
              <p className="text-white font-medium mb-1">Start Baking!</p>
              <p className="text-white/70 text-sm">
                The challenge now appears in "Your Challenges" section. Begin working toward the goals
                at your own pace.
              </p>
            </div>
          </li>
        </ol>

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-200 text-sm font-medium mb-1">You can join multiple challenges simultaneously</p>
              <p className="text-blue-200/80 text-xs">
                Work on several challenges at once if you're an active baker. Just make sure you can commit
                the time needed for each one.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="completing" title="Completing a Challenge">
        <p className="text-white/80 mb-4">
          To complete a challenge, you need to fulfill all its requirements:
        </p>

        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Track Your Progress
            </h4>
            <p className="text-white/70 text-sm mb-2">
              As you bake and complete objectives:
            </p>
            <ul className="text-white/60 text-sm space-y-1 ml-4">
              <li>• Upload photos of your completed bakes</li>
              <li>• Add notes about your experience</li>
              <li>• Mark milestones as complete in the challenge tracker</li>
              <li>• Share your results with the community</li>
            </ul>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              Submit for Completion
            </h4>
            <p className="text-white/70 text-sm">
              Once you've met all requirements, click "Complete Challenge" and submit your evidence (photos, notes, etc.).
              Your completion will be recorded and you'll earn recognition.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Earn Your Badge
            </h4>
            <p className="text-white/70 text-sm">
              Completed challenges appear in your profile's "Completed Challenges" section with a badge.
              Share your achievements and inspire other bakers!
            </p>
          </div>
        </div>
      </Section>

      <Section id="managing" title="Managing Your Challenges">
        <div className="space-y-4">
          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Your Challenges</h4>
            <p className="text-white/70 text-sm">
              All challenges you've joined appear in the "Your Challenges" section. You can:
            </p>
            <ul className="text-white/60 text-sm mt-2 space-y-1 ml-4">
              <li>• View your progress on each challenge</li>
              <li>• See deadlines and time remaining</li>
              <li>• Access challenge details and requirements</li>
              <li>• Track completion percentage</li>
            </ul>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Quitting a Challenge</h4>
            <p className="text-white/70 text-sm">
              Life happens! If you need to quit a challenge, open the challenge details and click "Quit Challenge".
              You won't lose any progress on other challenges, but you won't earn the badge for this one.
            </p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Viewing Completed Challenges</h4>
            <p className="text-white/70 text-sm">
              Your completed challenges are saved in the "Completed" section with:
            </p>
            <ul className="text-white/60 text-sm mt-2 space-y-1 ml-4">
              <li>• Your achievement badge</li>
              <li>• Completion date</li>
              <li>• Photos and notes from your submissions</li>
              <li>• Recognition from the community</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="leaderboards" title="Leaderboards & Rankings">
        <p className="text-white/80 mb-4">
          Some challenges feature leaderboards to add a competitive element:
        </p>

        <div className="bg-white/5 rounded-lg p-4 mb-4">
          <h4 className="text-white font-medium mb-2">How Rankings Work</h4>
          <p className="text-white/70 text-sm mb-3">
            Challenges with leaderboards rank participants based on:
          </p>
          <ul className="text-white/60 text-sm space-y-1 ml-4">
            <li>• Completion time (who finished first)</li>
            <li>• Quality ratings (based on analysis scores)</li>
            <li>• Community votes (if enabled)</li>
            <li>• Bonus objectives completed</li>
          </ul>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Trophy className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-200 text-sm font-medium mb-1">Top performers get special recognition</p>
              <p className="text-amber-200/80 text-xs">
                Finishing in the top 3 earns you a special badge and feature on the challenge's hall of fame!
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="creating" title="Creating Your Own Challenges (Admin)">
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-purple-200 text-sm">
                Challenge creation is currently limited to administrators. If you have an idea for a community challenge,
                reach out through the contact form and we'll consider featuring it!
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section id="tips" title="Pro Tips for Success">
        <ul className="space-y-3 text-white/70 text-sm">
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Read requirements carefully</span>
              Make sure you understand all objectives before starting. Some challenges have specific flour types,
              techniques, or time commitments.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Document everything</span>
              Take photos at each stage, not just the final result. Good documentation makes your submission stronger
              and helps you learn.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Don't rush</span>
              Quality matters more than speed. Take your time to do your best work rather than rushing to finish first.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Learn from others</span>
              Check out what other participants are doing. The community is here to help and inspire each other.
            </div>
          </li>
          <li className="flex items-start gap-3 bg-white/5 rounded-lg p-3">
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-white font-medium block mb-1">Start with beginner challenges</span>
              If you're new to sourdough or challenges, start with easier ones to build confidence and learn the system.
            </div>
          </li>
        </ul>
      </Section>
    </div>
  );
}
